var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { CorrelationVector, Log, LogCategory, Operation as LoggingOperation, SessionHealthEvent, SessionHealthImpact, SessionHealthReason, SessionHealthSource } from '@augloop/logging';
import { MessageEndpoint, MicroSyncMessage, SendMessageErrorReason, SessionCloseMessage, SessionInitMessage } from '@augloop/session-protocol';
import { SchemaObject } from '@augloop/types-core';
import { EventEmitter } from 'eventemitter3';
import { LogCountLimiter, Utils } from '../utils';
import { ClientSendMessageErrorReason, MessageQueue } from './message-queue';
import { ClosedState, DisconnectedState, InitingState, RunningState, SessionState } from './session-states';
import { WebSocketWorkerManager } from './websocket-worker-manager';
var WebSocketSessionManager = /** @class */ (function (_super) {
    __extends(WebSocketSessionManager, _super);
    function WebSocketSessionManager(url, stats, workerFactory, sessionInitializer, clientMetadata, tokenRefreshManager, settings) {
        var _a;
        var _this = _super.call(this) || this;
        _this.cvParent = new CorrelationVector();
        _this.logOp = new LoggingOperation({ operationName: 'SessionState', success: true }).start();
        _this.sessionStateChangeLogCountLimiter = new LogCountLimiter('SessionState');
        _this.stats = stats;
        _this.allStates = (_a = {},
            _a[SessionState.Initing] = new InitingState(_this),
            _a[SessionState.Running] = new RunningState(_this),
            _a[SessionState.Disconnected] = new DisconnectedState(_this),
            _a[SessionState.Closed] = new ClosedState(_this),
            _a);
        _this.setState(SessionState.Initing);
        _this.messageEndpoint = new MessageEndpoint('c'); // 'c' messageId prefix for client
        _this.messageEndpoint.setClientMetadata(clientMetadata);
        _this.messageEndpoint.setEgress(_this.egress.bind(_this));
        _this.messageEndpoint.onMessage(SessionCloseMessage.getTypeName(), function (message, done) { _this.onSessionCloseMessage(message, done); });
        _this.webSocketWorkerManager = new WebSocketWorkerManager(url, clientMetadata, workerFactory, sessionInitializer, _this.ingress.bind(_this), _this.onConnectionClose.bind(_this), function () { _this.onSessionClose(undefined, 'WSNotSupported'); }, settings);
        _this.messageQueue = new MessageQueue({
            // onResponse callback is created in retrySendMessage method so retry mechanism is preserved here
            // Bind cannot be used here because state can change after binding
            sendMessage: function (message, onResponse, attemptNumber, sendOnlyIfConnected) { return _this.state.sendMessage(message, onResponse, attemptNumber, sendOnlyIfConnected); },
            sendBytes: _this.sendBytes.bind(_this),
            canSendMessage: function () { return _this.state.canSendMessage(); },
            dropOnlySequencedSyncsOnReseed: settings.dropOnlySequencedSyncsOnReseedEnabled
        });
        sessionInitializer.on('connect', function (isSeedingRequired, isReconnect, sessionUrl, origin, authToken, workflowInputTypes) {
            // Set the session state to Running before emitting the 'connect' event that may trigger clients to send seed operations
            _this.setState(SessionState.Running, '', { isSessionReseedingStarted: isSeedingRequired && isReconnect });
            _this.emit('connect', isSeedingRequired, sessionUrl, origin, authToken, workflowInputTypes);
        });
        sessionInitializer.on('reconnect', function () { return _this.emit('reconnect'); });
        _this.resetNextSyncSequenceId = function () { return _this.emit('resetNextSyncSequenceId'); };
        _this.tokenRefreshManager = tokenRefreshManager;
        return _this;
    }
    WebSocketSessionManager.prototype.setState = function (newState, reason, params) {
        var _this = this;
        if (this.state && newState === this.state.stateName) {
            return;
        }
        var currentState = this.state ? SessionState[this.state.stateName] : 'undefined';
        // Transition from undefined to Initing allowed, otherwise check possibleNextStates
        var isTransitionPossible = this.state ? this.state.possibleNextStates.indexOf(newState) >= 0 : newState === SessionState.Initing;
        this.sessionStateChangeLogCountLimiter.log(function () {
            _this.logOp.stop();
            _this.logOp.resourceId = "New state: ".concat(isTransitionPossible ? SessionState[newState] : currentState);
            _this.logOp.resultDescription = "Previous state: ".concat(currentState);
            _this.logOp.dimension0 = "Attempted state: ".concat(SessionState[newState] || 'undefined');
            _this.logOp.resultSignature = reason + (_this.state && !isTransitionPossible ? 'Unexpected state change' : '');
            _this.logOp.success = isTransitionPossible;
            Log.info(0x2229d6db /* tag_8k311 */, LogCategory.CoreDefault, _this.logOp);
        });
        if (isTransitionPossible) {
            this.state = this.allStates[newState];
            // Reset logOp timer
            this.logOp.start();
            this.state.onEnter(params);
        }
    };
    WebSocketSessionManager.prototype.init = function (extensionConfigs, customInitPromise) {
        this.extensionConfigs = extensionConfigs;
        this.customInitPromise = customInitPromise;
        return this.webSocketWorkerManager.init(this.extensionConfigs, this.customInitPromise, true);
    };
    /**
     * Sends message immediately or queues it based on session state.
     * If request fails it retries it.
     */
    WebSocketSessionManager.prototype.sendMessage = function (message, onResponse, sendOnlyIfConnected) {
        this.retrySendMessage(message, onResponse, WebSocketSessionManager.maxRetries, sendOnlyIfConnected);
    };
    /**
     * Sends message immediately or queues it based on session state
     */
    WebSocketSessionManager.prototype.sendBytes = function (buffer) {
        this.state.sendBytes(buffer);
    };
    WebSocketSessionManager.prototype.onMessage = function (messageType, messageCallback) {
        this.messageEndpoint.onMessage(messageType, messageCallback);
    };
    WebSocketSessionManager.prototype.getCorrelationVector = function () {
        return this.cvParent;
    };
    WebSocketSessionManager.prototype.forceReconnect = function (extensionConfigs) {
        var _this = this;
        this.extensionConfigs = extensionConfigs || this.extensionConfigs;
        this.webSocketWorkerManager.close(false); // Note: Closing handshake is started but underlying websocket is not in closed state immediately after this function call ends. TODO 4977139: properly wait on close event before creating new connection.
        return new Promise(function (resolve) { return setTimeout(function () {
            resolve(_this.webSocketWorkerManager.init(_this.extensionConfigs));
        }, 100); });
    };
    WebSocketSessionManager.prototype.closeSession = function () {
        this.sendMessage(new SessionCloseMessage());
        this.webSocketWorkerManager.close(true);
        this.onSessionClose(undefined, 'ClientRequested');
    };
    WebSocketSessionManager.prototype.ingress = function (inputJson) {
        var _this = this;
        var input;
        try {
            input = JSON.parse(inputJson);
        }
        catch (e) {
            Log.error(0x2229d6dc /* tag_8k312 */, LogCategory.CoreDefault, new SessionHealthEvent({
                sessionHealthEventName: 'ProcessMessage',
                resourceId: 'Unknown',
                success: false,
                resultSignature: 'ParseError',
                resultDescription: e.message,
                durationMs: 0,
                source: SessionHealthSource.ClientRuntime,
                reason: SessionHealthReason.Client,
                impact: SessionHealthImpact.MissingOutput,
                affectedWorkflows: ['All'],
                message: ''
            }));
        }
        if (input) {
            this.messageEndpoint.ingress(input, function (error, response) { return _this.egress(error || response, function () { }); });
        }
    };
    WebSocketSessionManager.prototype.egress = function (output, done) {
        if (output) {
            this.webSocketWorkerManager.egress(output)
                .then(function () { return done(); })
                .catch(function (e) { return done(e); });
        }
    };
    WebSocketSessionManager.prototype.onSessionCloseMessage = function (message, done) {
        if (message.reconnectAllowed) {
            // just drop existing ws connection, do not close session permanently
            this.webSocketWorkerManager.close();
        }
        else {
            this.onSessionClose(message, 'CloseMessageReceived');
        }
        done();
    };
    WebSocketSessionManager.prototype.onSessionClose = function (message, reason) {
        this.setState(SessionState.Closed, reason);
        this.tokenRefreshManager.clearAllTimeouts();
        this.onConnectionClose(undefined);
        // Notify the host about session close unless the host closed the session
        if (message) {
            this.emit('sessionClose', message);
        }
    };
    WebSocketSessionManager.prototype.onConnectionClose = function (error) {
        this.state.onConnectionClose();
        this.stats.lastConnectionClose = Utils.getCurrentTimeMs();
        this.emit('disconnect', error);
    };
    /**
     * Sends message and if the error response is returned in the callback it retries sending it.
     * Retry is not triggered if the websocket is in disconnected state, messages are queued and sent when the stable connection is established.
     * This method does not wait between reties because messages get queued if there is no stable web socket connection.
     * @param message Message to send.
     * @param onResponse Response callback.
     * @param maxRetries Number of retries.
     * @param sendOnlyIfConnected If true message will be sent only if there is a connection with server (or it is already in process of establishing it) at the moment of calling.
     */
    WebSocketSessionManager.prototype.retrySendMessage = function (message, onResponse, maxRetries, sendOnlyIfConnected) {
        var _this = this;
        var attemptNumber = WebSocketSessionManager.maxRetries - maxRetries;
        this.state.sendMessage(message, function (err, response) {
            if (err && maxRetries > 0 && _this.canBeRetried(message) && _this.isTransientError(err)) {
                _this.retrySendMessage(message, onResponse, maxRetries - 1);
            }
            else if (onResponse) {
                onResponse(err, response);
            }
        }, attemptNumber, sendOnlyIfConnected);
    };
    WebSocketSessionManager.prototype.canBeRetried = function (message) {
        // SessionInitMessage is retried as part of WebSocketWorkerManager logic so no need to retry it here
        return !SchemaObject.matchesTypesFor(message, [SessionInitMessage.getTypeName(), MicroSyncMessage.getTypeName()]);
    };
    WebSocketSessionManager.prototype.isTransientError = function (err) {
        var errMsg = err.error;
        return errMsg !== SendMessageErrorReason.SyncMessageUnsupportedBatch
            && errMsg !== SendMessageErrorReason.UnexpectedSeedMessage
            && errMsg !== SendMessageErrorReason.UnsupportedSyncMessage
            && errMsg !== SendMessageErrorReason.AnnotationTokenNotFound
            && errMsg !== ClientSendMessageErrorReason.ArrivedBeforeReseeding
            && errMsg !== ClientSendMessageErrorReason.DroppedAsOldestInQueue;
    };
    WebSocketSessionManager.maxRetries = 2;
    return WebSocketSessionManager;
}(EventEmitter));
export { WebSocketSessionManager };
//# sourceMappingURL=websocket-session-manager.js.map